<template>
  <div class="card p-3">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="form-title">{{ route.params.id ? 'Editar' : 'Nova' }} Videoaula</h4>
      <button class="btn btn-sm btn-secondary" @click="voltar">Voltar</button>
    </div>

    <BaseForm
      :isFormValid="isFormValid"
      :isSubmitting="isSubmitting"
      @submit="salvarVideoaula"
      @cancel="voltar"
    >
      <div class="row g-3">
        <div class="col-md-5">
          <label for="titulo" class="form-label">Título *</label>
          <input
            id="titulo"
            v-model="videoaula.titulo"
            type="text"
            class="form-control"
            placeholder="Título"
            required
          />
        </div>

        <div class="col-md-6">
          <label for="descricao" class="form-label">Descrição</label>
          <input
            id="descricao"
            v-model="videoaula.descricao"
            type="text"
            class="form-control"
            placeholder="Descrição"
          />
        </div>

        <!-- ✅ Público com checkboxes (label acima do box) -->
        <div class="col-md-3">
          <label class="form-label">Público *</label>

          <div class="publicoBox mt-1">
            <div class="publicoGrid">
              <label class="publicoOption">
                <input
                  class="form-check-input m-0"
                  type="checkbox"
                  value="Instrutor"
                  v-model="videoaula.publico"
                />
                <span>Instrutor</span>
              </label>

              <label class="publicoOption">
                <input
                  class="form-check-input m-0"
                  type="checkbox"
                  value="Apoio"
                  v-model="videoaula.publico"
                />
                <span>Apoio</span>
              </label>

              <label class="publicoOption">
                <input
                  class="form-check-input m-0"
                  type="checkbox"
                  value="Diretoria"
                  v-model="videoaula.publico"
                />
                <span>Diretoria</span>
              </label>
            </div>
          </div>

          <small v-if="publicoErro" class="text-danger d-block mt-1">
            Selecione ao menos 1 público.
          </small>
        </div>

        <div class="col-md-4">
          <label for="video_url" class="form-label">Link *</label>
          <input
            id="video_url"
            v-model="videoaula.video_url"
            @input="updateThumbnail"
            type="url"
            class="form-control"
            placeholder="https://www.youtube.com/watch?v=ABC123xyz"
            required
          />
        </div>
      </div>

      <!-- Miniatura -->
      <div class="col-md-6" v-if="thumbnailUrl">
        <label class="form-label">Pré-visualização</label>
        <div>
          <img :src="thumbnailUrl" alt="Miniatura do vídeo" class="img-fluid rounded" />
        </div>
      </div>
    </BaseForm>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import BaseForm from '@/components/Layout/BaseForm.vue';
import axios from '@/services/axios';

export default {
  components: { BaseForm },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { appContext } = getCurrentInstance();
    const userId = appContext.config.globalProperties.$globalState.userId;

    // ✅ publico agora é array
    const videoaula = reactive({
      id: null,
      titulo: '',
      descricao: '',
      publico: [],
      video_url: '',
      usuario_id: userId
    });

    const isSubmitting = ref(false);
    const thumbnailUrl = ref(null);

    const publicoErro = computed(() => !videoaula.publico || videoaula.publico.length === 0);

    const isFormValid = computed(() => {
      return (
        videoaula.titulo.trim() !== '' &&
        Array.isArray(videoaula.publico) &&
        videoaula.publico.length > 0 &&
        videoaula.video_url.trim() !== ''
      );
    });

    function voltar() {
      router.push('/ajuda/videoaulas');
    }

    async function salvarVideoaula() {
      if (!isFormValid.value || isSubmitting.value) return;
      isSubmitting.value = true;

      try {
        const method = videoaula.id ? 'put' : 'post';
        const url = videoaula.id ? `/ajuda/videoaulas/${videoaula.id}` : '/ajuda/videoaulas';

        await axios[method](url, {
          titulo: videoaula.titulo,
          descricao: videoaula.descricao,
          // ✅ envia array (1..N)
          publico: videoaula.publico,
          video_url: videoaula.video_url,
          usuario_id: userId
        });

        voltar();
      } catch (e) {
        console.error('Erro:', e.response?.data || e);
      } finally {
        isSubmitting.value = false;
      }
    }

    function updateThumbnail() {
      const url = videoaula.video_url.trim();
      try {
        const u = new URL(url);
        let id = u.searchParams.get('v');
        if (!id && u.hostname === 'youtu.be') {
          id = u.pathname.slice(1);
        }
        thumbnailUrl.value = id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
      } catch {
        thumbnailUrl.value = null;
      }
    }

    function normalizarPublico(p) {
      if (Array.isArray(p)) return p.map(x => String(x).trim()).filter(Boolean);
      if (typeof p === 'string') {
        const s = p.trim();
        if (!s) return [];
        if (s.includes(',') || s.includes(';')) {
          return s.split(/[;,]/).map(x => x.trim()).filter(Boolean);
        }
        return [s];
      }
      return [];
    }
    
    onMounted(async () => {
      if (videoaula.video_url) updateThumbnail();

      const id = route.params.id;
      if (id) {
        try {
          const { data } = await axios.get(`/ajuda/videoaulas/${id}`);
          Object.assign(videoaula, data);
          videoaula.publico = normalizarPublico(data.publico);
          updateThumbnail();
        } catch (e) {
          console.error('Erro ao carregar:', e);
        }
      }
    });

    return {
      route,
      videoaula,
      isSubmitting,
      isFormValid,
      salvarVideoaula,
      voltar,
      thumbnailUrl,
      updateThumbnail,
      publicoErro
    };
  }
};
</script>

<style scoped>
.card {
  max-width: 90%;
  margin: 0 auto;
}
label.form-label {
  font-size: 0.7rem;
  padding: 0.0rem 0.2rem;
  margin-bottom: 0rem;
}
.img-fluid {
  max-height: 180px;
  object-fit: contain;
}

.publicoBox{
  border: 1px solid rgba(0,0,0,.12);
  border-radius: 0px;
  padding: 8px 10px;
  min-height: 86px;
  background: #fff;
}

.publicoGrid{
  display: grid;
  grid-template-columns: 1fr;
  gap: 2px;
}


.publicoOption{
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 8px;
  border-radius: 0px;
  cursor: pointer;
  user-select: none;
  line-height: 1;
}

.publicoOption:hover{
  background: rgba(0,0,0,.04);
}

</style>